<template>
    <!-- In-chat picker: the row is static and the action is its own button. -->
    <!-- min-h-14 keeps rows without a button as tall as those with one. -->
    <div v-if="actionButton" class="flex min-h-14 w-full items-center gap-3 py-3 ps-2.5" data-testid="picker-task-row">
        <span class="flex w-4 shrink-0 justify-center"><ProgressIcon :status="progress" /></span>
        <span class="grow min-w-0 body-small text-ink" :class="wrapLabel ? 'break-words' : 'truncate'" :title="wrapLabel ? undefined : review.cta?.label">
            {{ review.cta?.label }}
        </span>
        <PickerTaskAction
            :review="review"
            :current-conversation-hashid="currentConversationHashid"
            :is-busy="isBusy"
            :start-disabled="startDisabled"
            @activate="$emit('row-clicked', review)" />
    </div>
    <!-- Sidebar tracker: the whole row is the control, with a status chip. -->
    <button
        v-else
        type="button"
        class="group flex w-full items-center gap-3 rounded-md px-2.5 py-4 text-start transition-colors enabled:hover:bg-surface-hover disabled:cursor-default"
        :class="{ 'bg-surface-sunken': highlighted }"
        :disabled="isRowDisabled"
        :data-highlighted="highlighted || undefined"
        :data-testid="`picker-row-${buttonState.kind}`"
        @click="$emit('row-clicked', review)">
        <span class="grow min-w-0 body-small text-ink" :class="wrapLabel ? 'break-words' : 'truncate'" :title="wrapLabel ? undefined : review.cta?.label">
            {{ review.cta?.label }}
        </span>
        <StatusChip size="2xs" :state="chipState" class="shrink-0 justify-center" :class="{ 'min-w-24': !wrapLabel }">{{ buttonState.text }}</StatusChip>
    </button>
</template>

<script setup lang="ts">
import type { StatusChipState } from "~vue/components/StatusChip.vue";
import StatusChip from "~vue/components/StatusChip.vue";
import type { DecoratedPerformanceReview } from "~vue/types/performance_management";
import { computed } from "vue";
import { useI18n } from "vue-i18n";

import PickerTaskAction from "./pmPicker/PickerTaskAction.vue";
import ProgressIcon from "./pmPicker/ProgressIcon.vue";
import { taskAction, type TaskActionKind, taskProgress } from "./pmPicker/taskAction";

interface Props {
    review: DecoratedPerformanceReview;
    // Hashid of the coaching session the picker is rendered inside: its own row reads as the current one.
    currentConversationHashid?: string;
    isBusy?: boolean;
    // Sidebar tracker only: marks the row for the conversation the user is in.
    highlighted?: boolean;
    // Expired cycles allow View/Resume (plain navigation) but no new starts.
    startDisabled?: boolean;
    // The narrow sidebar wraps labels and lets chips shrink; the wider in-chat picker keeps one line.
    wrapLabel?: boolean;
    // The in-chat picker gives each row a neutral action button; the sidebar tracker makes the whole row the control.
    actionButton?: boolean;
    // Colour the chip by progress, as the PM page's status chips do; the in-chat picker stays neutral.
    statusColors?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
    currentConversationHashid: "",
    isBusy: false,
    highlighted: false,
    startDisabled: false,
    wrapLabel: false,
    actionButton: true,
    statusColors: false,
});

defineEmits<{ "row-clicked": [review: DecoratedPerformanceReview] }>();

const { t } = useI18n();

const buttonState = computed(() => taskAction(props.review, { currentConversationHashid: props.currentConversationHashid, startDisabled: props.startDisabled }, t));

const STATE_BY_KIND: Record<TaskActionKind, StatusChipState> = {
    start: "neutral",
    unavailable: "neutral",
    started: "active",
    resume: "active",
    view: "done",
    "marked-complete": "done",
};
const chipState = computed<StatusChipState>(() => (props.statusColors ? STATE_BY_KIND[buttonState.value.kind] : "neutral"));

const progress = computed(() => taskProgress(props.review));

const isRowDisabled = computed(
    () => props.isBusy || buttonState.value.kind === "started" || buttonState.value.kind === "marked-complete" || buttonState.value.kind === "unavailable",
);
</script>
